import { memo, useMemo } from 'react';
import { Mail, Phone } from 'lucide-react';
import HoverHint from '../ui/hoverHint';
import WebsiteIcon from '../find-investors/icons/website.svg';
import FacebookIcon from '../find-investors/icons/facebook.svg';
import LinkedInIcon from '../find-investors/icons/linkedin.svg';
import XIcon from '../find-investors/icons/x.svg';
import InstagramIcon from '../find-investors/icons/instagram.svg';
import {
  hasField,
  INVESTOR_CONTACT_HINT,
  INVESTOR_CONTACT_KEYS,
  INVESTOR_SOCIAL_HINT,
} from './helpers';

const ICON_SIZE = 15;
const ICON_BOX_CLASS = 'w-5 h-5';

const SOCIALS = [
  { key: 'website_url', icon: WebsiteIcon, alt: 'Website' },
  { key: 'fb_url', icon: FacebookIcon, alt: 'Facebook' },
  { key: 'linkedin_url', icon: LinkedInIcon, alt: 'LinkedIn' },
  { key: 'x_url', icon: XIcon, alt: 'X' },
  { key: 'inst_url', icon: InstagramIcon, alt: 'Instagram' },
];

const CONTACTS = [
  { key: 'phone', Icon: Phone, label: 'Phone (members only)' },
  { key: 'email', Icon: Mail, label: 'Email (members only)' },
];

const IconPill = memo(function IconPill({ children }) {
  return (
    <span className={`inline-flex items-center justify-center ${ICON_BOX_CLASS} align-middle`} aria-hidden="true">
      {children}
    </span>
  );
});

const IconWithHint = memo(function IconWithHint({ hint, label, children }) {
  return (
    <HoverHint message={hint}>
      <span aria-label={label}>{children}</span>
    </HoverHint>
  );
});

const InvestorAccessIcons = memo(function InvestorAccessIcons({
  investor,
  socialMode = 'hint',
  showContactIcons = true,
  className = 'flex items-center space-x-3',
  socialHint = INVESTOR_SOCIAL_HINT,
  contactHint = INVESTOR_CONTACT_HINT,
}) {
  const items = useMemo(() => {
    if (!investor) {
      return [];
    }

    const socialItems = SOCIALS
      .filter(({ key }) => hasField(investor, key))
      .map(({ key, icon, alt }) => {
        const iconNode = (
          <img
            src={icon}
            alt={alt}
            className={ICON_BOX_CLASS}
            width={ICON_SIZE}
            height={ICON_SIZE}
            loading="lazy"
            decoding="async"
          />
        );

        if (socialMode === 'direct' && investor[key]) {
          return (
            <a
              key={key}
              href={investor[key]}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={alt}
            >
              <IconPill>{iconNode}</IconPill>
            </a>
          );
        }

        return (
          <IconWithHint key={key} hint={socialHint} label={`${alt} (members only)`}>
            <IconPill>{iconNode}</IconPill>
          </IconWithHint>
        );
      });

    const contactItems = showContactIcons
      ? CONTACTS
          .filter(({ key }) => hasField(investor, key))
          .map(({ key, Icon, label }) => (
            <IconWithHint key={key} hint={contactHint} label={label}>
              <IconPill>
                <Icon size={ICON_SIZE} strokeWidth={1.75} aria-hidden="true" />
              </IconPill>
            </IconWithHint>
          ))
      : [];

    return [...socialItems, ...contactItems];
  }, [contactHint, investor, showContactIcons, socialHint, socialMode]);

  if (!items.length) {
    return null;
  }

  return <div className={className}>{items}</div>;
});

export function hasVisibleContactIcons(investor) {
  return INVESTOR_CONTACT_KEYS.some((key) => hasField(investor, key));
}

export default InvestorAccessIcons;
